/**
 * The snapshot ring, on both transports, behind one reader.
 *
 * Rust publishes into a ring of fixed-size slots in linear memory. What differs
 * between transports is only how those bytes reach the main thread:
 *
 * - **`sab`** — the worker copies the used bytes of the slot into a
 *   `SharedArrayBuffer` ring and stamps the control word with `Atomics`. The
 *   main thread then reads *views*, never copies, for as long as the page runs.
 * - **`copy`** — the worker copies the same bytes into a fresh `ArrayBuffer`
 *   and transfers it. The main thread parks it in its own slot array. One copy
 *   either way; what isolation buys is the per-frame read, not the publish.
 *
 * Deliberately not claimed: this is not yet zero-copy over *WASM linear memory
 * itself*. That needs the module's memory to be shared, which needs an
 * `+atomics` build of the standard library — a toolchain commitment B0.6 should
 * make against a measured number rather than B0.1 against an assumption
 * (docs/15-risks-questions.md, open question 20). The reader below does not
 * care: it already takes its offsets from `SnapshotLayout`, so the day the
 * backing becomes wasm memory, only `SabRing.publish` disappears.
 *
 * Tearing: the writer stamps the sequence number *after* the payload, so a
 * reader that sees a new sequence is looking at a slot that is already whole.
 * Three slots then give the renderer the two most recent published states —
 * what interpolation needs — while the worker writes a third, which at a 10 Hz
 * publish rate leaves ~200 ms before a slot the renderer is reading could be
 * reused. That is a headroom argument, not a lock: if publish rate and frame
 * rate ever converge, the ring grows or the reader takes a copy, and B0.6's
 * stress test is where that would show up.
 */

import {
  CTRL_LAST_BYTES,
  CTRL_LATEST_SEQ,
  CTRL_LATEST_SLOT,
  CTRL_PUBLISHES,
  CTRL_WORDS,
  type SnapshotLayout,
  type Transport,
} from './protocol';

/** One published slot, decoded. `instances` is `[x, y, z, tint]` per mover. */
export interface Snapshot {
  slot: number;
  seq: number;
  tick: number;
  count: number;
  speedMille: number;
  paused: boolean;
  instances: Float32Array;
  /**
   * The built site: `[x, z, w, d, height, class, type, flags]` per room and
   * tool, rooms first. Rebuilt by the renderer only when `structureEpoch`
   * moves, which is what lets a static layout ride in a per-frame snapshot
   * without costing a per-frame upload.
   */
  structures: Float32Array;
  structureCount: number;
  structureEpoch: number;
  /**
   * Lots in flight: `[x, z, state, progress]` each. `state` is
   * `0` walking · `1` queued · `2` running · `3` stuck, plus a half-step when
   * the lot has been expedited — the same trick the movers' tint uses.
   */
  lots: Float32Array;
  lotCount: number;
  /**
   * The floor plane, one byte per plot cell, row-major from the south-west
   * corner. Unlike the structures this cannot be skipped on a quiet frame: it
   * moves every tick, which is exactly why it is worth measuring at B0.6.
   */
  heat: Uint8Array;
  heatW: number;
  heatH: number;
  /**
   * Which field that plane carries (B0.5): `0` WIP, `1` utilization, `2`
   * waiting for people. Read from the slot rather than remembered on this side,
   * because the sim owns the choice — a renderer that painted the field it last
   * *asked* for would be a frame behind whenever the two disagreed.
   */
  heatView: number;
  /** Plot size in cells, so the renderer sizes the ground to the site. */
  plotW: number;
  plotH: number;
  /** Bytes this publish moved across the boundary. */
  byteLen: number;
}

/** What the main thread reads. Both transports satisfy it identically. */
export interface SnapshotSource {
  readonly transport: Transport;
  /** Sequence number of the newest complete publish, or 0 if none yet. */
  latestSeq(): number;
  /** The newest complete publish, or `null` before the first one lands. */
  latest(): Snapshot | null;
  /** The publish before the newest, for interpolation, or `null`. */
  previous(): Snapshot | null;
  /** Publishes seen since boot, as the source counts them. */
  publishes(): number;
}

const FLAG_PAUSED = 1;

/**
 * Decode a slot's header and payload sections out of a byte range.
 *
 * Exported so `scripts/boundary.mjs` can drive it headlessly: the one rule this
 * file exists to keep is that both transports are *one* code path, and the only
 * way to know that is to run both against the same publishes and diff them.
 */
export function decode(
  layout: SnapshotLayout,
  buffer: ArrayBufferLike,
  base: number,
  slot: number,
): Snapshot | null {
  const words = new Uint32Array(buffer, base, layout.headerBytes / 4);
  const seq = words[layout.wordSeq] ?? 0;
  if (seq === 0) return null;
  const count = words[layout.wordCount] ?? 0;
  const structureCount = words[layout.wordStructures] ?? 0;
  // Where each section begins moves with the one before it, so every offset is
  // read from the slot rather than computed here — the layout is Rust's to
  // state, and this side never recomputes it.
  const instanceOffset = words[layout.wordInstanceOffset] ?? layout.headerBytes;
  const lotCount = words[layout.wordLots] ?? 0;
  const lotOffset = words[layout.wordLotOffset] ?? instanceOffset;
  const heatOffset = words[layout.wordHeatOffset] ?? lotOffset;
  const heatW = words[layout.wordHeatW] ?? 0;
  const heatH = words[layout.wordHeatH] ?? 0;
  return {
    slot,
    seq,
    // The tick is 64-bit in the sim; the low word is all a display needs, and
    // at 10 Hz it does not wrap inside any session this prototype will run.
    tick: words[layout.wordTickLo] ?? 0,
    count,
    speedMille: words[layout.wordSpeedMille] ?? 1000,
    paused: ((words[layout.wordFlags] ?? 0) & FLAG_PAUSED) !== 0,
    instances: new Float32Array(buffer, base + instanceOffset, count * 4),
    structures: new Float32Array(buffer, base + layout.structureOffset, structureCount * 8),
    structureCount,
    structureEpoch: words[layout.wordStructureEpoch] ?? 0,
    lots: new Float32Array(buffer, base + lotOffset, lotCount * 4),
    lotCount,
    heat: new Uint8Array(buffer, base + heatOffset, heatW * heatH),
    heatW,
    heatH,
    heatView: words[layout.wordHeatView] ?? 0,
    plotW: words[layout.wordPlotW] ?? 0,
    plotH: words[layout.wordPlotH] ?? 0,
    byteLen: heatOffset + heatW * heatH,
  };
}

/* ------------------------------------------------------------------ *
 * SharedArrayBuffer transport
 * ------------------------------------------------------------------ */

/** The worker's half of the SAB path: copy a slot out of WASM memory. */
export class SabRingWriter {
  private readonly control: Int32Array;
  private readonly bytes: Uint8Array;

  constructor(
    private readonly layout: SnapshotLayout,
    control: SharedArrayBuffer,
    ring: SharedArrayBuffer,
  ) {
    this.control = new Int32Array(control, 0, CTRL_WORDS);
    this.bytes = new Uint8Array(ring);
  }

  /** `source` is a view over WASM linear memory for exactly this publish. */
  publish(slot: number, seq: number, source: Uint8Array): void {
    const base = slot * this.layout.slotBytes;
    this.bytes.set(source, base);
    // Payload first, then the control words — a reader that sees this sequence
    // number is looking at a slot that is already complete.
    Atomics.store(this.control, CTRL_LAST_BYTES, source.byteLength);
    Atomics.add(this.control, CTRL_PUBLISHES, 1);
    Atomics.store(this.control, CTRL_LATEST_SEQ, seq | 0);
    Atomics.store(this.control, CTRL_LATEST_SLOT, slot);
  }
}

/** The main thread's half of the SAB path: views, no copies. */
export class SabRingReader implements SnapshotSource {
  readonly transport: Transport = 'sab';
  private readonly control: Int32Array;

  constructor(
    private readonly layout: SnapshotLayout,
    control: SharedArrayBuffer,
    private readonly ring: SharedArrayBuffer,
  ) {
    this.control = new Int32Array(control, 0, CTRL_WORDS);
  }

  latestSeq(): number {
    return Atomics.load(this.control, CTRL_LATEST_SEQ) >>> 0;
  }

  publishes(): number {
    return Atomics.load(this.control, CTRL_PUBLISHES);
  }

  latest(): Snapshot | null {
    return this.read(Atomics.load(this.control, CTRL_LATEST_SLOT));
  }

  previous(): Snapshot | null {
    const latest = Atomics.load(this.control, CTRL_LATEST_SLOT);
    const before = (latest + this.layout.slotCount - 1) % this.layout.slotCount;
    return this.read(before);
  }

  private read(slot: number): Snapshot | null {
    if (slot < 0 || slot >= this.layout.slotCount) return null;
    return decode(this.layout, this.ring, slot * this.layout.slotBytes, slot);
  }
}

/* ------------------------------------------------------------------ *
 * postMessage-copy transport
 * ------------------------------------------------------------------ */

/**
 * The main thread's half of the copy path: keep the last `slotCount` transfers
 * and present exactly the interface the SAB reader does.
 */
export class CopyRingReader implements SnapshotSource {
  readonly transport: Transport = 'copy';
  private readonly slots: (ArrayBuffer | null)[];
  private latestSlot = -1;
  private seq = 0;
  private count = 0;

  constructor(private readonly layout: SnapshotLayout) {
    this.slots = new Array<ArrayBuffer | null>(layout.slotCount).fill(null);
  }

  /** Park a transferred slot. Called from the worker's message handler. */
  accept(slot: number, seq: number, bytes: ArrayBuffer): void {
    if (slot < 0 || slot >= this.layout.slotCount) return;
    this.slots[slot] = bytes;
    this.latestSlot = slot;
    this.seq = seq >>> 0;
    this.count += 1;
  }

  latestSeq(): number {
    return this.seq;
  }

  publishes(): number {
    return this.count;
  }

  latest(): Snapshot | null {
    return this.read(this.latestSlot);
  }

  previous(): Snapshot | null {
    if (this.latestSlot < 0) return null;
    return this.read((this.latestSlot + this.layout.slotCount - 1) % this.layout.slotCount);
  }

  private read(slot: number): Snapshot | null {
    if (slot < 0) return null;
    const buffer = this.slots[slot];
    if (!buffer) return null;
    // Transferred buffers hold only the used bytes, so they start at zero.
    return decode(this.layout, buffer, 0, slot);
  }
}
